import { useEffect, useState } from 'react';

import { PayPeriodType } from 'src/enums/PayPeriod';
import { DAY_SECONDS, differenceInDays, getFormattedDateString } from 'src/utils/date';
import { isUserGracePeriod, getPayPeriodCountdownTimerEndDate } from 'src/utils/engagementTimeSheets';
import { isDateInPayPeriod, toDateInterval } from 'src/utils/payPeriods';
import useUsers from 'src/hooks/useUsers';
import usePayPeriods from 'src/hooks/usePayPeriods';

type UsePayPeriodCountdownTimer = {
  remainingTime: number;
  daysDuration: number;
  payPeriodType: PayPeriodType;
  endDate: string;
};

const usePayPeriodCountdownTimer = (payPeriodDate: Date): UsePayPeriodCountdownTimer | null => {
  const [today, setToday] = useState<Date>(new Date());

  const { currentUser } = useUsers();
  const { currentPeriod, previousPeriod } = usePayPeriods();

  useEffect(() => {
    const handlePageVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setToday(new Date());
      }
    };

    document.addEventListener('visibilitychange', handlePageVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handlePageVisibilityChange);
    };
  }, []);

  if (!currentPeriod) return null;

  const isNowCurrentPayPeriod = isDateInPayPeriod(payPeriodDate, currentPeriod);
  const isNowGracePayPeriod =
    isUserGracePeriod(currentUser, currentPeriod) &&
    Boolean(previousPeriod && isDateInPayPeriod(payPeriodDate, previousPeriod));

  let payPeriodType = PayPeriodType.close;
  if (isNowCurrentPayPeriod) payPeriodType = PayPeriodType.current;
  if (isNowGracePayPeriod) payPeriodType = PayPeriodType.grace;

  const endDate = getPayPeriodCountdownTimerEndDate(payPeriodType, currentPeriod);
  const endDateString = getFormattedDateString(endDate, 'EEEE, MMMM dd, h:mm aaaa');

  const startTime = today.getTime() / 1000;
  const endTime = endDate.getTime() / 1000;

  const remainingTime = endTime - startTime;
  const daysDuration = differenceInDays(endDate, toDateInterval(currentPeriod).start) * DAY_SECONDS;

  return { remainingTime, daysDuration, payPeriodType, endDate: endDateString };
};

export default usePayPeriodCountdownTimer;
